/**
 * One hotel stay (a contiguous hotel + date-range booking). The header carries
 * the hotel name (links to the hotel guide), date range + nights, and a single
 * "Book N rooms" CTA plus an overflow menu to bulk-remove still-removable
 * rooms (with a confirmation dialog). Each room renders as its own row below.
 */

import type { ItineraryItem } from '@elara/client-core/models/ItineraryItemModel';

import React from 'react';

import { bookingCardSubtitleParts } from '@elara/client-core/lib/booking-metadata';
import { pluralize } from '@elara/client-core/lib/utils';

import { useItineraryState, useNavigate, useRouterState, useTripsState } from '#states/hooks.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '#ui/index.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

import { isBookable } from './booking-grouping.js';
import { BookingRow } from './BookingRow.js';
import { ROW_LIST_CLASSES } from './constants.js';
import { SectionHeader } from './SectionHeader.js';

export function HotelSection({ items }: { items: ItineraryItem[] }): React.ReactNode {
  const tripsState = useTripsState();
  const itineraryState = useItineraryState();
  const routerState = useRouterState();
  const { navigate } = useNavigate();
  const tripId = tripsState.current?.id;
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const lead = items[0];
  if (!lead) return null;
  const nights = lead.roomOption?.nightsCount;

  const bookableIds = items.filter(isBookable).map((i) => i.id);
  // Booked rooms and "pending" rooms (approved — the user has handed them to
  // the Elara team to book) are protected; only the rest can be bulk-removed.
  const removableRooms = items.filter((i) => !i.isBooked && !i.isApproved);

  async function deleteUnbookedRooms(): Promise<void> {
    if (!tripId) return;
    await Promise.all(
      removableRooms.map(async (room) => (room.id ? itineraryState.deleteItem(room.id, tripId) : undefined)),
    );
    setConfirmDeleteOpen(false);
  }

  // The stay's guide: the matched catalog hotel, else the Google Place attached to an externally-booked
  // stay with no catalog match (`guideRef` owns that hotel-then-place resolution).
  const guideRef = lead.guideRef;
  const hotelHref = guideRef
    ? routerState.buildUrlWithHashParams({ preview: guideRef.kind, id: guideRef.id })
    : undefined;

  function bookStay(): void {
    if (!tripId || bookableIds.length === 0) return;
    navigate(`/checkout/hotel/${tripId}?items=${bookableIds.join(',')}`);
  }

  return (
    <section>
      <SectionHeader
        icon="villa"
        title={
          hotelHref ? (
            <a href={hotelHref} className="hover:text-accent">
              {lead.hotelName ?? 'Hotel'}
            </a>
          ) : (
            (lead.hotelName ?? 'Hotel')
          )
        }
        subtitle={[lead.formattedDateRange, nights ? `${nights} ${pluralize(nights, 'night')}` : undefined].filter(
          (v): v is string => typeof v === 'string' && v.length > 0,
        )}
        action={
          bookableIds.length > 1 || removableRooms.length > 1 ? (
            <div className="flex items-center gap-2 md:pr-4">
              {bookableIds.length > 1 && (
                <Button onClick={bookStay}>
                  <MaterialIcon icon="shopping_cart_checkout" size={IconSizes.sm} />
                  <span>
                    Book {bookableIds.length} {pluralize(bookableIds.length, 'room')}
                  </span>
                </Button>
              )}
              {removableRooms.length > 1 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="icon" size="icon-sm">
                      <MaterialIcon icon="more_vert" size={IconSizes.sm} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => setTimeout(() => setConfirmDeleteOpen(true), 50)}
                      className="text-destructive"
                    >
                      <MaterialIcon icon="delete" className="mr-2" size={IconSizes.sm} />
                      Remove {removableRooms.length} {pluralize(removableRooms.length, 'room')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ) : undefined
        }
      />
      <div className={ROW_LIST_CLASSES}>
        {items.map((item) => (
          <BookingRow key={item.id} item={item} />
        ))}
      </div>

      <Dialog open={confirmDeleteOpen} onOpenChange={(open) => !open && setConfirmDeleteOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Remove {removableRooms.length} {pluralize(removableRooms.length, 'room')} from{' '}
              {lead.hotelName ?? 'this hotel'}?
            </DialogTitle>
            <DialogDescription>
              These rooms haven&apos;t been booked or sent to our team, and will be permanently removed from your
              itinerary.
            </DialogDescription>
          </DialogHeader>
          <ul className="divide-y rounded-md border text-sm">
            {removableRooms.map((room) => (
              <li key={room.id} className="flex items-start justify-between gap-3 px-3 py-2">
                <div className="min-w-0">
                  <div className="break-words">{room.displayTitle}</div>
                  {bookingCardSubtitleParts(room).length > 0 && (
                    <div className="text-xs text-muted-foreground">{bookingCardSubtitleParts(room).join(' · ')}</div>
                  )}
                </div>
                {room.cost && <span className="shrink-0 tabular-nums">{room.formattedCost}</span>}
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void deleteUnbookedRooms()}>
              Remove {removableRooms.length} {pluralize(removableRooms.length, 'room')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
